import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getSession, onAuthStateChange } from '../../features/auth/api/authUtils';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const queryClient = useQueryClient();

  const handlingSession = useRef(false);

  const handleSession = async (currentSession, source = 'unknown') => {
    if (handlingSession.current) {
      console.log(`[AuthProvider] handleSession already in progress, skipping ${source}`);
      return;
    }

    try {
      handlingSession.current = true;
      console.log(`[AuthProvider] Handling session from ${source} for user:`, currentSession?.user?.email);

      setSession(currentSession);
    } catch (error) {
      console.error(`[AuthProvider] Error in handleSession (source: ${source}):`, error);
    } finally {
      handlingSession.current = false;
      if (!initialized) {
        console.log('[AuthProvider] Setting initialized to true');
        setInitialized(true);
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    let initialCheckDone = false;

    // Firebase owns token renewal. A second timer can replay a
    // rotated refresh token and sign out newer sessions in other tabs.
    const initializeAuth = async () => {
      try {
        const s = await getSession();
        if (mounted) {
          // Tenant access is account-specific. Never reuse a cached membership
          // from the Google account that was active before a redirect/reload.
          if (s?.user) {
            queryClient.removeQueries({ queryKey: ['currentUserAccess'] });
          }
          await handleSession(s, 'initial-check');
          initialCheckDone = true;
        }
      } catch (error) {
        console.error('[AuthProvider] Error getting initial session:', error);
        if (mounted) {
          setInitialized(true);
        }
      }
    };

    const {
      data: { subscription },
    } = onAuthStateChange(async (event, currentAuthSession) => {
      if (mounted && initialCheckDone) {
        console.log(
          '[AuthProvider] Auth state changed:',
          event,
          'Session user:',
          currentAuthSession?.user?.email
        );

        await handleSession(currentAuthSession, `auth-state-${event.toLowerCase()}`);

        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          console.log('[AuthProvider] Invalidating user-specific queries due to', event);
          queryClient.invalidateQueries({ queryKey: ['adminAccount'] });
          queryClient.removeQueries({ queryKey: ['currentUserAccess'] });

          if (event === 'SIGNED_IN') {
            queryClient.refetchQueries({ queryKey: ['currentUserAccess'] });
          }
        }
      }
    });

    initializeAuth();

    return () => {
      mounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const value = {
    session,
    isAuthenticated: !!session?.user,
    user: session?.user ?? null,
    initialized,
  };

  if (!initialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400" />
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
